import {
  append,
  converge,
  curry,
  defaultTo,
  equals,
  find,
  identity,
  lensPath,
  lensProp,
  over,
  pipe,
  propEq,
  remove,
  set,
  update,
  view,
} from "ramda";
import { caseInsensitiveSort } from "util/sorts";
import { actionTypes } from "edit-media/redux/editMediaActions";
import { MEDIA_ITEM_UPDATED } from "application/redux/applicationActionTypes";
import {
  validateContributorField,
  validateEditMediaField,
  validateEditMedia,
  validateContributor,
} from "edit-media/editMediaValidation";

const initialState = {
  activeTabIndex: 0,
  contributorDialog: {
    disableSave: true,
    errors: {},
    fields: {
      role: {},
    },
    index: -1,
    visible: false,
  },
  thumbnailDialog: {
    visible: false,
    thumbnailProcessing: false,
    thumbnailFile: null,
  },
  categories: [],
  contributorRoles: [],
  contributorTypes: [],
  disableSave: true,
  errors: [],
  languages: [],
  licenseTypes: [],
  loading: true,
  mediaItem: {},
  originalMediaItem: {},
  removeContributorDialog: {
    visible: false,
    index: -1,
  },
  visible: false,
};

const activeTabIndexLens = lensProp("activeTabIndex");
const setActiveTabIndex = set(activeTabIndexLens);
export const getActiveTabIndex = view(activeTabIndexLens);

const categoriesLens = lensProp("categories");
const setCategories = set(categoriesLens);
export const getCategories = pipe(view(categoriesLens), defaultTo([]));

const contributorRolesLens = lensProp("contributorRoles");
const setContributorRoles = set(contributorRolesLens);
export const getContributorRoles = pipe(
  view(contributorRolesLens),
  defaultTo([]),
);

const contributorTypesLens = lensProp("contributorTypes");
const setContributorTypes = set(contributorTypesLens);
export const getContributorTypes = pipe(
  view(contributorTypesLens),
  defaultTo([]),
);

const disableSaveLens = lensProp("disableSave");
const setDisableSave = set(disableSaveLens);
export const getDisableSave = view(disableSaveLens);

const errorsLens = lensProp("errors");
const setErrors = set(errorsLens);
export const getErrors = pipe(view(errorsLens), defaultTo([]));

const languagesLens = lensProp("languages");
const setLanguages = set(languagesLens);
export const getLanguages = pipe(view(languagesLens), defaultTo([]));

const licenseTypesLens = lensProp("licenseTypes");
const setLicenseTypes = set(licenseTypesLens);
export const getLicenseTypes = pipe(view(licenseTypesLens), defaultTo([]));

const loadingLens = lensProp("loading");
const setLoading = set(loadingLens);
export const getLoading = view(loadingLens);

const mediaItemLens = lensProp("mediaItem");
const setMediaItem = set(mediaItemLens);
export const getMediaItem = view(mediaItemLens);

const mediaItemContributorsLens = lensPath(["mediaItem", "contributors"]);
export const getMediaItemContributors = pipe(
  view(mediaItemContributorsLens),
  defaultTo([]),
);
export const getMediaItemContributor = curry(
  (index, state) => getMediaItemContributors(state)[index] || {},
);
const addMediaItemContributor = curry((contributor, state) =>
  over(mediaItemContributorsLens, append(contributor), state),
);
const updateMediaItemContributor = curry((index, contributor, state) =>
  over(mediaItemContributorsLens, update(index, contributor), state),
);
const removeMediaItemContributor = curry((index, state) =>
  over(mediaItemContributorsLens, remove(index, 1), state),
);

const originalMediaItemLens = lensProp("originalMediaItem");
const setOriginalMediaItem = set(originalMediaItemLens);
export const getOriginalMediaItem = view(originalMediaItemLens);

const visibleLens = lensProp("visible");
const setVisible = set(visibleLens);
export const getVisible = view(visibleLens);

const mediaItemFieldsLens = lensPath(["mediaItem"]);
const setMediaItemField = curry((path, value, state) =>
  set(lensPath(["mediaItem", ...path]), value, state),
);
export const getMediaItemFields = view(mediaItemFieldsLens);

const contributorDialogLens = lensProp("contributorDialog");
const setContributorDialog = set(contributorDialogLens);

const contributorDialogDisableSaveLens = lensPath([
  "contributorDialog",
  "disableSave",
]);
const setContributorDialogDisableSave = set(contributorDialogDisableSaveLens);
export const getContributorDialogDisableSave = view(
  contributorDialogDisableSaveLens,
);

const contributorDialogIndexLens = lensPath(["contributorDialog", "index"]);
const setContributorDialogIndex = set(contributorDialogIndexLens);
export const getContributorDialogIndex = pipe(
  view(contributorDialogIndexLens),
  defaultTo(-1),
);

const contributorDialogVisibleLens = lensPath(["contributorDialog", "visible"]);
const setContributorDialogVisible = set(contributorDialogVisibleLens);
export const getContributorDialogVisible = view(contributorDialogVisibleLens);

const contributorDialogFieldsLens = lensPath(["contributorDialog", "fields"]);
const setContributorDialogFields = set(contributorDialogFieldsLens);
const setContributorDialogField = curry((path, value, state) =>
  set(lensPath(["contributorDialog", "fields", ...path]), value, state),
);
export const getContributorDialogFields = view(contributorDialogFieldsLens);

const contributorDialogErrorsLens = lensPath(["contributorDialog", "errors"]);
const setContributorDialogErrors = set(contributorDialogErrorsLens);
export const getContributorDialogErrors = view(contributorDialogErrorsLens);

const removeContributorDialogIndexLens = lensPath([
  "removeContributorDialog",
  "index",
]);
const setRemoveContributorDialogIndex = set(removeContributorDialogIndexLens);
export const getRemoveContributorDialogIndex = view(
  removeContributorDialogIndexLens,
);

const removeContributorDialogVisibleLens = lensPath([
  "removeContributorDialog",
  "visible",
]);
const setRemoveContributorDialogVisible = set(
  removeContributorDialogVisibleLens,
);
export const getRemoveContributorDialogVisible = view(
  removeContributorDialogVisibleLens,
);

const thumbnailDialogVisibleLens = lensPath(["thumbnailDialog", "visible"]);
const setThumbnailDialogVisible = set(thumbnailDialogVisibleLens);
export const getThumbnailDialogVisible = view(thumbnailDialogVisibleLens);

const thumbnailFileLens = lensPath(["thumbnailDialog", "thumbnailFile"]);
const setThumbnailFile = set(thumbnailFileLens);
export const getThumbnailFile = view(thumbnailFileLens);

const thumbnailProcessingLens = lensPath([
  "thumbnailDialog",
  "thumbnailProcessing",
]);
const setThumbnailProcessing = set(thumbnailProcessingLens);
export const getThumbnailProcessing = view(thumbnailProcessingLens);

const nameSort = caseInsensitiveSort("displayName");

const checkValidationErrors = (path, value) => state =>
  setErrors(validateEditMediaField(path, value, getErrors(state)), state);

const checkContributorDialogValidationErrors = (field, value) => state =>
  setContributorDialogErrors(
    validateContributorField(field, value, getContributorDialogErrors(state)),
    state,
  );

const contributorDialogRoleChanged = (id, state) => {
  const role = find(propEq("id", id), getContributorRoles(state));
  return pipe(setContributorDialogField(["role"], role))(state);
};

const hideRemoveContributorDialog = state =>
  pipe(
    setRemoveContributorDialogVisible(false),
    setRemoveContributorDialogIndex(initialState.removeContributorDialog.index),
  )(state);

const updateDisableSave = state => {
  const mediaItem = getMediaItem(state);
  const originalMediaItem = getOriginalMediaItem(state);
  const errors = validateEditMedia(mediaItem);
  const disableSave =
    errors !== undefined || equals(mediaItem, originalMediaItem);

  return setDisableSave(disableSave, state);
};

const updateContributorDialogDisableSave = state => {
  const contributor = getContributorDialogFields(state);
  const originalContributor = getMediaItemContributor(
    getContributorDialogIndex(state),
    state,
  );
  const errors = validateContributor(contributor);
  const disableSave =
    errors !== undefined || equals(contributor, originalContributor);

  return setContributorDialogDisableSave(disableSave, state);
};

const editMediaReducer = (state = initialState, action) => {
  switch (action.type) {
    case actionTypes.BLUR_MEDIA_ITEM_FIELD:
      return pipe(
        checkValidationErrors(action.field, action.value),
        updateDisableSave,
      )(state);

    case actionTypes.CONTRIBUTOR_DIALOG_FIELD_BLURRED:
      return pipe(
        checkContributorDialogValidationErrors(action.field, action.value),
        updateContributorDialogDisableSave,
      )(state);

    case actionTypes.CONTRIBUTOR_DIALOG_FIELD_CHANGED:
      return pipe(
        setContributorDialogField(action.path, action.value),
        checkContributorDialogValidationErrors(action.path, action.value),
        updateContributorDialogDisableSave,
      )(state);

    case actionTypes.CONTRIBUTOR_DIALOG_ROLE_CHANGED:
      return contributorDialogRoleChanged(action.id, state);

    case actionTypes.FETCH_VOCABULARY_COMPLETE:
      return pipe(
        pipe(nameSort, setLanguages)(action.languages),
        pipe(nameSort, setLicenseTypes)(action.licenseTypes),
        pipe(nameSort, setContributorRoles)(action.contributorRoles),
        pipe(nameSort, setContributorTypes)(action.contributorTypes),
        setCategories(action.categories),
        setLoading(false),
      )(state);

    case actionTypes.HIDE_CONTRIBUTOR_DIALOG:
      return setContributorDialogVisible(false, state);

    case actionTypes.HIDE_REMOVE_CONTRIBUTOR_DIALOG:
      return hideRemoveContributorDialog(state);

    case actionTypes.HIDE_DRAWER:
      return pipe(
        setVisible(false),
        setLoading(true),
        setErrors([]),
        setDisableSave(true),
      )(state);

    case actionTypes.MEDIA_ITEM_FIELD_CHANGED:
      return pipe(
        setMediaItemField(action.path, action.value),
        checkValidationErrors(action.path, action.value),
        updateDisableSave,
      )(state);

    case MEDIA_ITEM_UPDATED:
      return pipe(setOriginalMediaItem(action.mediaItem), updateDisableSave)(
        state,
      );

    case actionTypes.REMOVE_CONTRIBUTOR:
      return pipe(
        removeMediaItemContributor(action.index),
        hideRemoveContributorDialog,
        updateDisableSave,
      )(state);

    case actionTypes.SAVE_ADD_CONTRIBUTOR:
      return pipe(
        converge(addMediaItemContributor, [
          getContributorDialogFields,
          identity,
        ]),
        setContributorDialogVisible(false),
        updateDisableSave,
      )(state);

    case actionTypes.SAVE_EDIT_CONTRIBUTOR:
      return pipe(
        converge(updateMediaItemContributor, [
          getContributorDialogIndex,
          getContributorDialogFields,
          identity,
        ]),
        setContributorDialogVisible(false),
        updateDisableSave,
      )(state);

    case actionTypes.SAVE_MEDIA_ITEM:
      return setDisableSave(true, state);

    case actionTypes.SAVE_MEDIA_ITEM_COMPLETE:
      return pipe(setDisableSave(false))(state);

    case actionTypes.SELECT_ACTIVE_TAB:
      return setActiveTabIndex(action.index, state);

    case actionTypes.SHOW_ADD_CONTRIBUTOR_DIALOG:
      return pipe(
        setContributorDialog(initialState.contributorDialog),
        setContributorDialogVisible(true),
      )(state);

    case actionTypes.SHOW_EDIT_CONTRIBUTOR_DIALOG:
      return pipe(
        setContributorDialog(initialState.contributorDialog),
        setContributorDialogIndex(action.index),
        setContributorDialogFields(
          getMediaItemContributor(action.index, state),
        ),
        setContributorDialogVisible(true),
      )(state);

    case actionTypes.SHOW_DRAWER:
      return pipe(
        setMediaItem(action.mediaItem),
        setOriginalMediaItem(action.mediaItem),
        setVisible(true),
      )(state);

    case actionTypes.SHOW_REMOVE_CONTRIBUTOR_DIALOG:
      return pipe(
        setRemoveContributorDialogIndex(action.index),
        setRemoveContributorDialogVisible(true),
      )(state);

    case actionTypes.SHOW_EDIT_THUMBNAIL_DIALOG:
      return pipe(
        setThumbnailDialogVisible(true),
        setThumbnailFile(initialState.thumbnailDialog.thumbnailFile),
      )(state);

    case actionTypes.HIDE_EDIT_THUMBNAIL_DIALOG:
      return pipe(
        setThumbnailDialogVisible(false),
        setThumbnailFile(initialState.thumbnailDialog.thumbnailFile),
      )(state);

    case actionTypes.SELECT_THUMBNAIL_FILE:
      return pipe(setThumbnailFile(action.file))(state);

    case actionTypes.SAVE_THUMBNAIL_FILE:
      return pipe(
        setThumbnailDialogVisible(false),
        setThumbnailProcessing(true),
      )(state);

    case actionTypes.THUMBNAIL_UPDATE_COMPLETE:
      return pipe(
        setMediaItemField(["thumbnailUrl"], action.thumbnailUrl),
        setThumbnailProcessing(false),
      )(state);

    default:
      return state;
  }
};

export default editMediaReducer;
